import { Check, Circle, CircleAlert, CircleDashed, Hourglass, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { StayPlanStatus } from "@/hooks/useStayPlanStatus";
import {
  buildPlanMeter,
  type PlanMeter,
  type PlanPipState,
} from "@/components/packages/plan-card/planPips";
import { PLAN_TONE } from "@/components/packages/plan-card/planTone";
import { formatDateBR } from "@/lib/date-utils";
import { buildCycleWeeks, type CycleDayState, type CycleWeek } from "@/components/packages/plan-card/cycleWeeks";

export interface PlanBalanceMeterProps {
  status: StayPlanStatus;
  /** Diárias não usadas acumulam para o próximo ciclo. */
  carryoverEnabled: boolean;
}

const PIP_CLASS: Record<PlanPipState, string> = {
  used: "border-primary bg-primary text-primary-foreground",
  scheduled: "border-state-warning/60 bg-state-warning-soft text-state-warning-foreground",
  available: "border-dashed border-foreground/25 bg-background text-foreground/25",
  adjustment: "border-amber-400 bg-state-warning-soft text-state-warning-foreground",
};

/** Na trilha de ajuste a diária gasta continua sendo de ajuste: âmbar, não laranja. */
const ADJUSTMENT_PIP_CLASS: Record<PlanPipState, string> = {
  ...PIP_CLASS,
  used: "border-amber-400 bg-amber-400 text-state-warning-foreground",
};

const CYCLE_DAY_CLASS: Record<CycleDayState, string> = {
  used: PIP_CLASS.used,
  scheduled: PIP_CLASS.scheduled,
  available: PIP_CLASS.available,
  extra: "border-brand-purple/50 bg-brand-purple/10 text-brand-purple",
};

const LEGEND_SWATCH: Record<PlanPipState | "extra", string> = {
  extra: "border-[1.5px] border-brand-purple/50 bg-brand-purple/10",
  used: "bg-primary",
  scheduled: "border-[1.5px] border-dotted border-state-warning bg-state-warning-soft",
  available: "border-[1.5px] border-dashed border-foreground/30 bg-background",
  adjustment: "border-[1.5px] border-amber-400 bg-state-warning-soft",
};

/**
 * O ciclo inteiro, uma linha por semana. É o que responde "quando o pet vem
 * neste mês" sem abrir a agenda; a semana corrente fica destacada.
 */
function CycleWeeksGrid({ weeks }: { weeks: CycleWeek[] }) {
  const { t } = useTranslation("patients");

  return (
    <div className="min-w-0 flex-1">
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-color-light">
        {t("planCard.cycleWeeksLabel")}
      </p>
      <ul className="mt-2 grid gap-1.5" aria-label={t("planCard.cycleWeeksLabel")}>
        {weeks.map((week) => (
          <li
            key={week.start}
            data-week={week.start}
            data-current={week.isCurrent || undefined}
            className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-2 py-1 ${week.isCurrent ? "bg-cream-100" : ""}`}
          >
            <span className="w-[5.5rem] shrink-0 text-xs text-muted-foreground">
              {t("planCard.weekRow", { date: formatDateBR(week.start).slice(0, 5) })}
            </span>
            {week.isOffWeek && week.days.length === 0 ? (
              <span className="text-xs text-color-light">{t("planCard.offWeek")}</span>
            ) : null}
            {week.days.map((day, index) => (
              <span
                key={`${day.date ?? "free"}-${index}`}
                data-pip={day.state}
                title={`${t(`planCard.pip.${day.state}`)}${day.date ? ` · ${formatDateBR(day.date)}` : ""}`}
                className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[10px] border-[1.5px] text-[0.6875rem] font-semibold ${CYCLE_DAY_CLASS[day.state]}`}
              >
                {day.date ? formatDateBR(day.date).slice(0, 2) : ""}
                <span className="sr-only">
                  {t(`planCard.pip.${day.state}`)}
                  {day.date ? ` ${formatDateBR(day.date)}` : ""}
                </span>
              </span>
            ))}
            {week.isCurrent ? <span className="sr-only">{t("planCard.currentWeek")}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PipIcon({ pip }: { pip: PlanPipState }) {
  if (pip === "used") return <Check className="h-4 w-4" aria-hidden />;
  if (pip === "scheduled") return <CircleDashed className="h-3.5 w-3.5" aria-hidden />;
  if (pip === "adjustment") return <Plus className="h-4 w-4" aria-hidden />;
  return <Circle className="h-3.5 w-3.5" aria-hidden />;
}

/**
 * Uma trilha de marcadores. A cota e o ajuste ficam em trilhas separadas porque
 * seguem relógios diferentes: a cota semanal reinicia toda segunda-feira, o
 * ajuste vale até o fim do ciclo.
 */
function PipLane({
  lane,
  label,
  pips,
  classes,
}: {
  lane: "quota" | "overflow" | "adjustment";
  label: string;
  pips: PlanPipState[];
  classes: Record<PlanPipState, string>;
}) {
  const { t } = useTranslation("patients");

  return (
    <div>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-color-light">{label}</p>
      <ul data-lane={lane} className="mt-2 flex flex-wrap gap-[7px]" aria-label={label}>
        {pips.map((pip, index) => (
          <li
            key={index}
            data-pip={pip}
            title={t(`planCard.pip.${pip}`)}
            className={`flex h-[34px] w-[34px] items-center justify-center rounded-[11px] border-[1.5px] ${classes[pip]}`}
          >
            <PipIcon pip={pip} />
            <span className="sr-only">{t(`planCard.pip.${pip}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** O rodapé do medidor: o que acontece com o que sobrou. */
function MeterHint({
  status,
  meter,
  carryoverEnabled,
}: {
  status: StayPlanStatus;
  meter: PlanMeter;
  carryoverEnabled: boolean;
}) {
  const { t } = useTranslation("patients");
  const hasAdjustment = meter.adjustmentPips.some((pip) => pip === "adjustment");
  const cycleEnd = status.cycle ? formatDateBR(status.cycle.end) : "";

  if (meter.kind === "week") {
    const quotaReached = meter.pips.every((pip) => pip === "used");
    const warn = quotaReached && !hasAdjustment;
    const key = quotaReached
      ? hasAdjustment
        ? "planCard.weekQuotaReachedWithAdjustment"
        : "planCard.weekQuotaReached"
      : hasAdjustment
        ? "planCard.weekHintWithAdjustment"
        : "planCard.weekHint";

    return (
      <p
        className={`mt-[18px] flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs leading-relaxed ${
          quotaReached
            ? "border-amber-200 bg-state-warning-soft text-state-warning-foreground"
            : "border-border bg-cream-50 text-muted-foreground"
        }`}
      >
        {warn || quotaReached ? (
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <Hourglass className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        )}
        {t(key, { count: meter.adjustmentPips.filter((pip) => pip === "adjustment").length, date: cycleEnd })}
      </p>
    );
  }

  return (
    <p className="mt-[18px] flex items-start gap-2 rounded-md border border-border bg-cream-50 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
      <Hourglass className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      {meter.kind === "package"
        ? t("planCard.packageHint")
        : carryoverEnabled
          ? t("planCard.carryoverOn")
          : t("planCard.carryoverOff")}
    </p>
  );
}

export function PlanBalanceMeter({ status, carryoverEnabled }: PlanBalanceMeterProps) {
  const { t } = useTranslation("patients");
  const { contract } = status;
  const meter = buildPlanMeter(status);
  const tone = PLAN_TONE[contract.module];

  // Sem cota, semana ou pacote reconhecidos: não há o que medir em pips.
  if (!meter) {
    return (
      <div>
        <p className="text-sm font-medium text-foreground">
          {contract.usageRule
            ? t(`daycarePlans.usageRule.${contract.usageRule}${contract.usageRule === "per_week" && contract.weekInterval === 2 ? "_biweekly" : ""}`, { limit: contract.usageLimit ?? 0 })
            : t("planCard.noBalance")}
        </p>
        {status.usageSummary ? (
          <p className="mt-1 text-sm text-muted-foreground">
            {t("planCard.usedThisWeek", { used: status.usageSummary.used, limit: status.usageSummary.limit })}
          </p>
        ) : null}
      </div>
    );
  }

  const isWeek = meter.kind === "week";
  const quotaUsed = meter.pips.filter((pip) => pip === "used").length;
  const hasOverflowLane = meter.overflowScheduledPips.length > 0;
  const hasAdjustmentLane = meter.adjustmentPips.length > 0;
  const cycleEnd = status.cycle ? formatDateBR(status.cycle.end) : "";
  const cycleWeeks = isWeek && status.cycle && status.weeklyQuota
    ? buildCycleWeeks({
        cycle: status.cycle,
        today: status.weeklyQuota.week.start,
        usages: contract.usages,
        weeklyLimit: status.weeklyQuota.limit,
        startsOn: contract.startsOn,
        weekInterval: contract.weekInterval ?? 1,
      })
    : null;
  const legend: (PlanPipState | "extra")[] = isWeek
    ? ["used", "scheduled", "available", "extra"]
    : meter.kind === "cycle"
    ? ["used", "scheduled", "available"]
    : hasAdjustmentLane
      ? ["used", "available", "adjustment"]
      : ["used", "available"];

  return (
    <div>
      {isWeek && status.weeklyQuota ? (
        <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-color-light">
          {t("planCard.weekOf", {
            start: formatDateBR(status.weeklyQuota.week.start),
            end: formatDateBR(status.weeklyQuota.week.end),
          })}
        </p>
      ) : null}

      <div className={`flex flex-wrap items-end gap-x-4 gap-y-2 ${isWeek ? "mt-2.5" : ""}`}>
        <p className="flex items-end gap-2">
          <span className={`font-display text-[3.5rem] font-extrabold leading-[0.9] tracking-tight ${tone.accent}`}>
            {meter.remaining}
          </span>
          <span className="whitespace-pre-line font-display text-base font-bold leading-tight text-foreground">
            {t("planCard.daysAvailable")}
          </span>
        </p>
        <p className="text-[0.8125rem] leading-relaxed text-muted-foreground">
          {meter.kind === "package"
            ? t("planCard.ofTotalInPackage", { total: meter.total })
            : isWeek
              ? t("planCard.ofTotalInWeek", { total: meter.pips.length })
              : t("planCard.ofTotalInCycle", { total: meter.total })}
          <br />
          {t("planCard.usedCount", { used: isWeek ? quotaUsed : meter.used })}
          {meter.adjustmentUnits > 0 ? ` · ${t("planCard.fromAdjustment", { count: meter.adjustmentUnits })}` : ""}
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-start gap-4">
        {cycleWeeks ? (
          <CycleWeeksGrid weeks={cycleWeeks} />
        ) : (
          <PipLane lane="quota" label={t("planCard.meterLabel")} pips={meter.pips} classes={PIP_CLASS} />
        )}
        {hasOverflowLane && !cycleWeeks ? (
          <>
            <span className="hidden self-stretch border-e border-foreground/10 sm:block" aria-hidden />
            <PipLane
              lane="overflow"
              label={t("planCard.overflowScheduledLane")}
              pips={meter.overflowScheduledPips}
              classes={PIP_CLASS}
            />
          </>
        ) : null}
        {hasAdjustmentLane ? (
          <>
            <span className="hidden self-stretch border-e border-foreground/10 sm:block" aria-hidden />
            <PipLane
              lane="adjustment"
              label={t("planCard.adjustmentLane", { date: cycleEnd })}
              pips={meter.adjustmentPips}
              classes={ADJUSTMENT_PIP_CLASS}
            />
          </>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-[18px] gap-y-1 text-xs text-muted-foreground">
        {legend.map((pip) => (
          <span key={pip} className="flex items-center gap-1.5">
            <span
              className={`flex h-[11px] w-[11px] items-center justify-center rounded-[4px] ${LEGEND_SWATCH[pip]}`}
              aria-hidden
            >
              {pip === "scheduled" ? (
                <span className="block h-[5px] w-[5px] rounded-full border-[1px] border-dotted border-state-warning" />
              ) : null}
            </span>
            {t(`planCard.pipShort.${pip}`)}
          </span>
        ))}
      </div>

      <MeterHint status={status} meter={meter} carryoverEnabled={carryoverEnabled} />
    </div>
  );
}
